/**
 * @CODE:SPEC-UTM-001 - UTM Parameter Tracking Module
 * Extracts, stores, and manages UTM parameters for attribution tracking
 *
 * Requirements:
 * - REQ-001: Extract UTM parameters from URL query string
 * - REQ-002: Store using First-touch/Last-touch attribution strategy
 * - REQ-003: Use localStorage with cookie fallback, 30-day expiration
 * - REQ-004: Integrate with Cart API via attributes
 * - REQ-005: Ensure data persists to order metadata
 * - REQ-006: Privacy-compliant (no PII, first-party only)
 * - REQ-007: Performance optimized (<50ms, <10ms main thread)
 */

(function(window, document) {
  'use strict';

  // Default configuration
  const DEFAULT_CONFIG = {
    storageKey: 'syrs_utm_data',
    expirationDays: 30,
    trackingMode: 'first_touch', // 'first_touch' or 'last_touch'
  };

  /**
   * UTM Tracker - Core module for UTM parameter management
   */
  const UTMTracker = {
    config: DEFAULT_CONFIG,

    /**
     * Initialize UTM tracker with optional configuration
     * @param {Object} config - Configuration object
     */
    init: function(config = {}) {
      this.config = { ...DEFAULT_CONFIG, ...config };
      this.handleUTMTracking();
    },

    /**
     * REQ-001: Extract UTM parameters from URL query string
     * @param {URLSearchParams|String} searchParams - Optional search parameters (for testing)
     * @returns {Object} Object containing utm_source, utm_medium, utm_campaign, utm_content, utm_term
     */
    extractUTMParams: function(searchParams) {
      const params = {};
      const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

      try {
        // Use provided searchParams or fall back to window.location.search
        const search = searchParams || window.location.search;
        const urlParams = new URLSearchParams(search);
        utmKeys.forEach(key => {
          const value = urlParams.get(key);
          if (value !== null) {
            params[key] = value;
          }
        });
      } catch (error) {
        console.error('[UTM Tracker] Error extracting UTM parameters:', error);
      }

      return params;
    },

    /**
     * REQ-001: Capture complete attribution data including UTM params, landing page, and timestamp
     * @returns {Object} Complete UTM data with metadata
     */
    captureUTMData: function() {
      const utmParams = this.extractUTMParams();

      // Only return data if there are UTM parameters
      if (Object.keys(utmParams).length === 0) {
        return null;
      }

      const data = {
        version: '1.0',
        ...utmParams,
        landing_page: window.location.pathname,
        captured_at: new Date().toISOString(),
      };

      // Add referrer if available
      if (document.referrer) {
        data.referrer = document.referrer;
      }

      return data;
    },

    /**
     * Check if stored UTM data has expired (30 days)
     * @param {Object} data - Stored UTM data
     * @returns {Boolean} True if data has expired
     */
    isExpired: function(data) {
      if (!data || !data.expires_at) {
        return true;
      }

      try {
        const expiresDate = new Date(data.expires_at);
        return expiresDate < new Date();
      } catch (error) {
        return true;
      }
    },

    /**
     * REQ-002: Handle UTM data storage with first-touch/last-touch strategy
     * @param {Object} options - Options for tracking
     */
    handleUTMTracking: function(options = {}) {
      const trackingMode = options.trackingMode || this.config.trackingMode;
      const newData = this.captureUTMData();

      // No new UTM parameters to process
      if (!newData) {
        return;
      }

      const existingData = this.getStoredData();

      // Check if existing data is still valid
      const hasValidData = existingData && !this.isExpired(existingData);

      // First-touch: only save if no existing data
      if (trackingMode === 'first_touch' && hasValidData) {
        return;
      }

      // Last-touch: always overwrite
      this.saveUTMData(newData);
    },

    /**
     * REQ-003: Save UTM data to storage (localStorage primary, cookie fallback)
     * @param {Object} data - UTM data to store
     */
    saveUTMData: function(data) {
      // Add expiration date
      const expirationDate = new Date();
      expirationDate.setDate(expirationDate.getDate() + this.config.expirationDays);
      data.expires_at = expirationDate.toISOString();

      const serialized = JSON.stringify(data);

      // Try localStorage first
      try {
        window.localStorage.setItem(this.config.storageKey, serialized);
        return;
      } catch (error) {
        // localStorage failed, try cookie as fallback
        console.warn('[UTM Tracker] localStorage not available, falling back to cookie');
      }

      // Fallback to cookie
      try {
        this.setCookie(this.config.storageKey, serialized, this.config.expirationDays);
      } catch (error) {
        console.error('[UTM Tracker] Failed to save UTM data:', error);
      }
    },

    /**
     * REQ-003: Get stored UTM data from localStorage or cookie
     * @returns {Object|null} Stored UTM data or null
     */
    getStoredData: function() {
      try {
        const stored = window.localStorage.getItem(this.config.storageKey);
        if (stored) {
          return JSON.parse(stored);
        }
      } catch (error) {
        console.warn('[UTM Tracker] Error reading from localStorage:', error);
      }

      // Try cookie as fallback
      try {
        const cookieValue = this.getCookie(this.config.storageKey);
        if (cookieValue) {
          return JSON.parse(cookieValue);
        }
      } catch (error) {
        console.warn('[UTM Tracker] Error reading from cookie:', error);
      }

      return null;
    },

    /**
     * Helper: Set cookie with expiration
     * @param {String} name - Cookie name
     * @param {String} value - Cookie value
     * @param {Number} days - Expiration in days
     */
    setCookie: function(name, value, days = 30) {
      try {
        const expires = new Date();
        expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
        document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
      } catch (error) {
        console.error('[UTM Tracker] Error setting cookie:', error);
      }
    },

    /**
     * Helper: Get cookie value
     * @param {String} name - Cookie name
     * @returns {String|null} Cookie value or null
     */
    getCookie: function(name) {
      try {
        const nameEQ = name + '=';
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
          let cookie = cookies[i].trim();
          if (cookie.indexOf(nameEQ) === 0) {
            return decodeURIComponent(cookie.substring(nameEQ.length));
          }
        }
      } catch (error) {
        console.error('[UTM Tracker] Error reading cookie:', error);
      }
      return null;
    },

    /**
     * REQ-004: Get stored UTM data
     * @returns {Object|null} Stored UTM data
     */
    getUTMData: function() {
      const data = this.getStoredData();

      // Check if data is expired
      if (data && this.isExpired(data)) {
        try {
          window.localStorage.removeItem(this.config.storageKey);
        } catch (error) {
          // Ignore removal errors
        }
        return null;
      }

      return data;
    },

    /**
     * REQ-004: Convert UTM data to Cart API attributes format
     * @returns {Object} Cart attributes in format { _attribution_utm_source: value, ... }
     */
    getCartAttributes: function() {
      const utmData = this.getUTMData();
      const attributes = {};

      if (!utmData) {
        return attributes;
      }

      // Map UTM fields to cart attribute keys
      const fieldMap = {
        utm_source: '_attribution_utm_source',
        utm_medium: '_attribution_utm_medium',
        utm_campaign: '_attribution_utm_campaign',
        utm_content: '_attribution_utm_content',
        utm_term: '_attribution_utm_term',
        landing_page: '_attribution_landing_page',
        captured_at: '_attribution_timestamp',
      };

      Object.entries(fieldMap).forEach(([utmKey, attrKey]) => {
        if (utmData[utmKey] !== undefined && utmData[utmKey] !== null && utmData[utmKey] !== '') {
          attributes[attrKey] = utmData[utmKey];
        }
      });

      return attributes;
    },

    /**
     * Remove stored UTM data
     */
    clearUTMData: function() {
      try {
        window.localStorage.removeItem(this.config.storageKey);
      } catch (error) {
        // Ignore removal errors
      }
    },
  };

  // Expose to global scope
  window.UTMTracker = UTMTracker;

  // Auto-initialize on page load if script is loaded (skip in test environment)
  if (typeof jest === 'undefined') {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        UTMTracker.init();
      });
    } else {
      // DOM is already loaded
      UTMTracker.init();
    }
  }

})(window, document);
